import { Suspense } from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { AdminRoutes } from "./AdminRoutes";
import { WorkplaceRoutes } from "./WorkplaceRoutes";
import { WorkerRoutes } from "./WorkerRoutes";

// Public routes - accessible regardless of session type
const ReferenceForm = loadLazily(
  async () =>
    await import("@src/containers/referenceForm").then((module) => ({
      default: module.ReferenceForm,
    }))
);

const ShiftDetailsPage = loadLazily(
  async () =>
    await import("@src/components/ShiftDetails/ShiftDetailsPage").then((module) => ({
      default: module.ShiftDetailsPage,
    }))
);

const WorkerLanding = loadLazily(async () => await import("@src/containers/workerLanding"));

const ShiftSignoutFeedbackForm = loadLazily(
  async () =>
    await import("@src/containers/ShiftSignoutFlow/shiftSignoutFlowForm").then((module) => ({
      default: module.ShiftSignoutFlowForm,
    }))
);

const PublicWorkplacePlacementsPage = loadLazily(
  async () =>
    await import("@src/appV2/PublicWorkplacePlacements/Page").then((module) => ({
      default: module.PublicWorkplacePlacementsPage,
    }))
);

export interface AllRoutesProps {
  admin: boolean;
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
  renderStartPage: () => JSX.Element | null;
}

export function AllRoutes(props: AllRoutesProps) {
  const { admin, isAdmin, isWorker, isWorkplace, renderStartPage } = props;

  const hasSession = isAdmin || isWorker || isWorkplace;

  // If user has no session (logged out), redirect to login page
  // This prevents users from getting stuck on protected routes after logout
  if (!hasSession) {
    return <Redirect to="/login" />;
  }

  return (
    <Suspense fallback={<></>}>
      <Switch>
        <Route exact path="/" render={renderStartPage} />

        <Route exact path="/enter" render={renderStartPage} />

        {/* Public routes - accessible regardless of session type */}
        <Route path={routes.reference} component={ReferenceForm} />
        <Route exact path={routes.shiftDetails} component={ShiftDetailsPage} />
        <Route exact path={routes.workers} component={WorkerLanding} />
        <Route path={routes.shiftSignoutFeedback} component={ShiftSignoutFeedbackForm} />
        <Route
          exact
          path={routes.publicWorkplacePlacements}
          component={PublicWorkplacePlacementsPage}
        />

        {/* Admin routes must come before workplace routes when user is pseudo-logged in */}
        {/* Use admin (has admin privilege) OR isAdmin (currently logged in as admin) */}
        {(admin || isAdmin) && <AdminRoutes />}

        {isWorkplace && <WorkplaceRoutes />}

        {isWorker && <WorkerRoutes />}

        {/* Catch-all route: redirect unrecognized routes to the default view */}
        <Route render={renderStartPage} />
      </Switch>
    </Suspense>
  );
}
